/* T530 oikeusnäkymä (Toni 12.10.2026 «HYVÄKSYN Päächatin ehdotukset»: Oikeusnakyma_suunnitelma_2026-10-12.md, 12 suositusta).
   Ylimpänä tiivistelmä, sitten paketit («Tee hänestä …») ja yksittäiset oikeudet «Lisäasetukset»-kohdan takana ryhmittäin.
   Kohde valitaan yhdellä valitsimella «Koko yritys / Valitut kohteet» — ei ruuturiviä per oikeus, joten näkymän korkeus ei
   kasva kohteiden mukana. Jokainen myöntö on yhä nimetylle tunnukselle, ja myöntäjä ja aika tallentuvat palvelimella. */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, type Kohde, type Oikeustila, type OikeuspakettiLuettelo, type OikeuspakettiMaar } from "./api";
import { kantaTeksti, t, virheTeksti, type KaannosAvain } from "./kieli";

type Oikeus = { tunnus: string; nimi: string; kuvaus: string | null; kohderajattava: boolean; moduuli?: string | null; moduuli_kaytossa?: boolean };
const ARKALUONTEISET = "arkaluonteiset_henkilotiedot";
const SULJETUT = new Set(["valmis", "keskeytetty", "peruuntunut"]);
let PAKETIT_VALIMUISTI: OikeuspakettiLuettelo | null = null;

const pakettiNimi = (p: string) => t(`oik.paketti.${p}.nimi` as KaannosAvain);
const roolinNimi = (r: string) => t(`rooli.${r}` as KaannosAvain);
const oikeusNimi = (luettelo: Oikeus[], o: string) => kantaTeksti("oikeus", o, "nimi", luettelo.find((x) => x.tunnus === o)?.nimi ?? o);

/** Kohdejoukko tekstinä: null = koko yritys; yli kolme → «A, B, C + N». */
function KohdeTeksti({ idt, kohteet }: { idt: number[] | null; kohteet: Kohde[] }) {
  if (idt === null) return <>{t("oik.koko_yritys")}</>;
  const nimet = idt.map((x) => kohteet.find((k) => k.id === x)?.tunnus ?? `#${x}`);
  if (nimet.length <= 3) return <>{nimet.join(", ")}</>;
  return <details className="oik__kohteet" style={{ display: "inline" }}><summary>{nimet.slice(0, 3).join(", ")} + {nimet.length - 3}</summary>{nimet.join(", ")}</details>;
}

/** ⓘ: pieni pyöreä kuvake (24 px, kosketusalue 44 px), avautuu ponnahdusikkunaksi vieressään ja sulkeutuu Escillä tai
 *  ulkopuolelle napauttamalla (Tonin katselmointi 12.10.2026: kuvaus vain ⓘ:n takana). Sama paketeille ja oikeuksille. */
export function InfoNappi({ nimi, children }: { nimi: string; children: ReactNode }) {
  const [auki, setAuki] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!auki) return;
    const ulos = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) setAuki(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAuki(false); };
    document.addEventListener("mousedown", ulos); document.addEventListener("touchstart", ulos); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", ulos); document.removeEventListener("touchstart", ulos); document.removeEventListener("keydown", esc); };
  }, [auki]);
  return (
    <span className="info" ref={ref}>
      <button type="button" className="info__nappi" aria-label={t("oik.lisatietoa_x", { nimi })} title={t("oik.lisatietoa_x", { nimi })}
              aria-expanded={auki} onClick={() => setAuki(!auki)} data-info={nimi}><span aria-hidden="true">i</span></button>
      {auki && <span className="info__ikkuna" role="dialog" aria-label={nimi} data-info-ikkuna="">{children}</span>}
    </span>
  );
}

/** «Koko yritys / Valitut kohteet ▾» — monivalinta hakuineen, suljetut piilossa (paitsi valitut tai «näytä suljetut»). */
export function KohdeValitsin({ arvo, muuta, kohteet, rajattava }: {
  arvo: number[] | null; muuta: (x: number[] | null) => void; kohteet: Kohde[]; rajattava: boolean;
}) {
  const [haku, setHaku] = useState("");
  const [suljetut, setSuljetut] = useState(false);
  if (!rajattava) return <span className="dim" data-kohdevalitsin="koko">{t("oik.koko_yritys")}</span>;
  const nakyvat = kohteet.filter((k) => (suljetut || !SULJETUT.has(k.tila) || (arvo ?? []).includes(k.id))
    && (!haku || `${k.tunnus} ${k.nimi}`.toLowerCase().includes(haku.toLowerCase())));
  return (
    <div className="oik__valitsin" data-kohdevalitsin={arvo === null ? "koko" : "valitut"}>
      <select value={arvo === null ? "koko" : "valitut"} onChange={(e) => muuta(e.target.value === "koko" ? null : [])}
              data-kohdevalinta="">
        <option value="koko">{t("oik.koko_yritys")}</option>
        <option value="valitut">{t("oik.valitut_kohteet")}{arvo && arvo.length ? ` (${t("oik.valittu_n", { n: arvo.length })})` : ""}</option>
      </select>
      {arvo !== null && (
        <div className="oik__monivalinta">
          <input type="search" placeholder={t("oik.hae_kohde")} value={haku} onChange={(e) => setHaku(e.target.value)} data-kohdehaku="" />
          <div className="oik__lista">
            {nakyvat.map((k) => {
              const on = arvo.includes(k.id);
              return (
                <label key={k.id} className={"valinta" + (on ? " valinta--on" : "")} data-kohde={k.id}>
                  <input type="checkbox" checked={on} onChange={() => muuta(on ? arvo.filter((x) => x !== k.id) : [...arvo, k.id])} />
                  {k.tunnus}{SULJETUT.has(k.tila) ? ` · ${k.tila}` : ""}
                </label>
              );
            })}
          </div>
          <label className="dim" style={{ fontWeight: 400 }}>
            <input type="checkbox" checked={suljetut} onChange={() => setSuljetut(!suljetut)} data-nayta-suljetut="" /> {t("oik.nayta_suljetut")}
          </label>
          {arvo.length === 0 && <p className="hint">{t("oik.ei_kohteita")}</p>}
        </div>
      )}
    </div>
  );
}

export function OikeusEditori({ kayttajaId, oikeusluettelo, kohteet, tunnukset, paivita, ilmoita }: {
  kayttajaId: number; oikeusluettelo: Oikeus[]; kohteet: Kohde[];
  /** Kopioinnin lähteet (tunnuksen id ja nimi). */
  tunnukset: { id: number; nimi: string }[];
  paivita: () => Promise<void>; ilmoita: (t: string | null) => void;
}) {
  const [tila, setTila] = useState<Oikeustila | null>(null);
  const [luettelo, setLuettelo] = useState<OikeuspakettiLuettelo | null>(PAKETIT_VALIMUISTI);
  const [auki, setAuki] = useState<string | null>(null);          // paketti jota muokataan
  const [kohdeArvo, setKohdeArvo] = useState<number[] | null>(null);
  const [lisaaRooli, setLisaaRooli] = useState(false);
  const [peruste, setPeruste] = useState("");
  const [oikAuki, setOikAuki] = useState<string | null>(null);    // yksittäinen oikeus jota muokataan
  const [oikArvo, setOikArvo] = useState<number[] | null>(null);
  const [haku, setHaku] = useState("");
  const [kopio, setKopio] = useState<{ lahde: number | null; esikatselu: Awaited<ReturnType<typeof api.kopioiOikeudet>> | null } | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  const lataa = useCallback(async () => {
    const [ti, lu] = await Promise.all([api.oikeustila(kayttajaId), PAKETIT_VALIMUISTI ? Promise.resolve(PAKETIT_VALIMUISTI) : api.oikeuspaketit()]);
    PAKETIT_VALIMUISTI = lu; setTila(ti); setLuettelo(lu);
  }, [kayttajaId]);
  useEffect(() => { void lataa(); }, [lataa]);

  async function teko(f: () => Promise<unknown>, onnistui?: string) {
    setTallennetaan(true); ilmoita(null);
    try { await f(); ilmoita(onnistui ?? t("oik.tallennettu")); setAuki(null); setOikAuki(null); setKopio(null); await lataa(); await paivita(); }
    catch (e) { ilmoita(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.muutos_virhe")); }
    finally { setTallennetaan(false); }
  }

  const meta = useMemo(() => new Map(oikeusluettelo.map((o) => [o.tunnus, o])), [oikeusluettelo]);
  if (!tila || !luettelo) return <p className="dim">…</p>;

  /* Yksittäiset rivit oikeuksittain: yksittäin-lähde → laajuus (null = koko yritys), ja mistä paketeista rivi on. */
  const yksittain = new Map<string, number[] | null>();
  const paketista = new Map<string, Set<string>>();
  for (const r of tila.oikeusrivit) {
    if (r.yksittain) { const v = yksittain.get(r.oikeus); yksittain.set(r.oikeus, r.kohde_id === null || v === null ? null : [...(v ?? []), r.kohde_id]); }
    for (const p of r.paketit) (paketista.get(r.oikeus) ?? paketista.set(r.oikeus, new Set()).get(r.oikeus)!).add(p);
  }
  const annettuja = new Set(tila.oikeusrivit.filter((r) => r.oikeus !== ARKALUONTEISET).map((r) => r.oikeus)).size;

  const tiivistelma = [
    ...tila.paketit.map((p) => <span key={"p" + p.id} data-tiivistelma-paketti={p.paketti}><strong>{pakettiNimi(p.paketti)}</strong> (<KohdeTeksti idt={p.kohde_idt} kohteet={kohteet} />)</span>),
    ...[...yksittain].filter(([o]) => o !== ARKALUONTEISET).map(([o, idt]) => <span key={"o" + o} data-tiivistelma-oikeus={o}>{oikeusNimi(oikeusluettelo, o)} (<KohdeTeksti idt={idt} kohteet={kohteet} />)</span>),
  ];

  function avaaPaketti(p: OikeuspakettiMaar) {
    const voimassa = tila!.paketit.find((x) => x.paketti === p.tunnus);
    setAuki(p.tunnus); setKohdeArvo(voimassa ? voimassa.kohde_idt : null); setLisaaRooli(false); setPeruste(""); setOikAuki(null);
  }
  const ryhmat = luettelo.ryhmajarjestys.filter((r) => r !== "henkilotiedot");
  const oikeudetRyhmassa = (r: string) => oikeusluettelo.filter((o) => o.tunnus !== ARKALUONTEISET && (luettelo.ryhmat[o.tunnus] ?? "osaaminen") === r
    && (!haku || `${oikeusNimi(oikeusluettelo, o.tunnus)} ${kantaTeksti("oikeus", o.tunnus, "kuvaus", o.kuvaus ?? "")}`.toLowerCase().includes(haku.toLowerCase())));

  return (
    <div className="oik" data-oikeuseditori={kayttajaId}>
      {/* Tiivistys (12.10.2026): tyhjänä ei omaa lohkoa; annettuna yksi rivi «Erilliset oikeudet: …». */}
      {tiivistelma.length > 0 && (
        <p className="oik__tiivistelma tun__rivi" data-oikeus-tiivistelma="">
          <span className="tun__nimi">{t("kay.erilliset_oikeudet")}:</span>
          <span>{tiivistelma.reduce<React.ReactNode[]>((a, x, i) => (i ? [...a, " · ", x] : [x]), [])}</span>
        </p>
      )}

      <h4 className="tun__ryhmaotsikko" data-tee-hanesta="">{t("oik.tee_hanesta")} <InfoNappi nimi={t("oik.tee_hanesta")}><span>{t("oik.tee_vihje")}</span></InfoNappi></h4>
      {/* 12.10.2026 (Tonin katselmointi, pääChatin muotoilema): paketit ruudukkona (1 sarake puhelimella, 2–3 leveällä),
          annetut ensin reunapalkilla, ei käytettävissä olevat yhdelle riville syyn mukaan — ei isoina harmaina kortteina. */}
      {(() => {
        const kaytettavissa = luettelo.paketit.filter((p) => tila.paketit.some((x) => x.paketti === p.tunnus) || !p.laji || tila.laji === p.laji)
          .sort((a, b) => Number(tila.paketit.some((x) => x.paketti === b.tunnus)) - Number(tila.paketit.some((x) => x.paketti === a.tunnus)));
        const eiTalle = luettelo.paketit.filter((p) => !kaytettavissa.includes(p));
        const syyt = new Map<string, string[]>();
        for (const p of eiTalle) { const s = p.laji === "ulkopuolinen" ? t("oik.laji_ulkopuolinen") : t("oik.laji_henkilo"); syyt.set(s, [...(syyt.get(s) ?? []), pakettiNimi(p.tunnus)]); }
        return (<>
      <div className="oik__paketit">
        {kaytettavissa.map((p) => {
          const voimassa = tila.paketit.find((x) => x.paketti === p.tunnus);
          const rooli = p.rooli ?? (tila.roolit.length ? null : p.ehdotettuRooli ?? null);
          const rooliPuuttuu = !!rooli && !tila.roolit.includes(rooli);
          const eiRajattavat = p.oikeudet.filter((o) => meta.get(o) && !meta.get(o)!.kohderajattava);
          const puuttuvat = p.oikeudet.filter((o) => !meta.get(o));
          const rajattava = p.oikeudet.some((o) => meta.get(o)?.kohderajattava);
          return (
            <div key={p.tunnus} className={"oik__paketti" + (voimassa ? " oik__paketti--on" : "")}
                 data-paketti={p.tunnus} data-paketti-voimassa={voimassa ? "1" : "0"}>
              <div className="oik__rivi">
                <strong>{pakettiNimi(p.tunnus)}</strong>
                <InfoNappi nimi={pakettiNimi(p.tunnus)}>
                  <span data-paketti-tieto={p.tunnus}>{t(`oik.paketti.${p.tunnus}.kuvaus` as KaannosAvain)}</span>
                  {/* Toni 12.10.2026 «ⓘ T530-erään hyväksytty: oikeuslista ⓘ:n taakse. Pelkän roolin paketeissa ⓘ kertoo, mitä
                      rooli antaa»: sama oikeuden nimi kuin Lisäasetuksissa, rivi per oikeus. Rooliteksti on luettu reittien
                      roolivartijoista (tarkastaja: tarkastukset, VT, painekoe, punch, linjatarkastus, nippu, liitteet). */}
                  {p.oikeudet.length > 0 ? (
                    <ul className="info__lista" data-paketti-oikeudet={p.tunnus}>
                      {p.oikeudet.map((o) => <li key={o} data-oikeus={o}>{oikeusNimi(oikeusluettelo, o)}</li>)}
                    </ul>
                  ) : p.rooli ? (
                    <span data-paketti-rooli-antaa={p.rooli}>{t(`oik.rooli_antaa.${p.rooli}` as KaannosAvain, { rooli: roolinNimi(p.rooli) })}</span>
                  ) : <span>{t("oik.ei_oikeuksia")}</span>}
                  {rooli && <span className="info__lisa">{t("oik.vaatii_roolin_lyhyt", { rooli: roolinNimi(rooli) })}</span>}
                </InfoNappi>
              </div>
              {voimassa && <div className="oik__tila" data-paketti-kohteet="">{t("oik.voimassa")}: <KohdeTeksti idt={voimassa.kohde_idt} kohteet={kohteet} /></div>}
              {auki === p.tunnus ? (
                <div className="oik__muokkaus" data-paketti-muokkaus={p.tunnus}>
                  {rajattava && <KohdeValitsin arvo={kohdeArvo} muuta={setKohdeArvo} kohteet={kohteet} rajattava />}
                  {rooliPuuttuu && (
                    <label className={"valinta" + (lisaaRooli ? " valinta--on" : "")} data-paketti-rooli={rooli!}>
                      <input type="checkbox" checked={lisaaRooli} onChange={() => setLisaaRooli(!lisaaRooli)} /> {t("oik.vaatii_roolin", { rooli: roolinNimi(rooli!) })}
                    </label>
                  )}
                  {kohdeArvo !== null && eiRajattavat.length > 0 && <p className="hint" data-koskee-koko-yritysta="">{t("oik.koskee_koko_yritysta", { oikeudet: eiRajattavat.map((o) => oikeusNimi(oikeusluettelo, o)).join(", ") })}</p>}
                  {puuttuvat.length > 0 && <p className="hint">{puuttuvat.join(", ")}</p>}
                  <input placeholder={t("oik.peruste")} value={peruste} onChange={(e) => setPeruste(e.target.value)} maxLength={500} />
                  <div className="oik__napit">
                    <button className="nappi" data-paketti-tallenna={p.tunnus}
                      disabled={tallennetaan || (rooliPuuttuu && !lisaaRooli) || (kohdeArvo !== null && kohdeArvo.length === 0) || puuttuvat.length > 0}
                      onClick={() => void teko(() => api.myonnaPaketti({ kayttaja_id: kayttajaId, paketti: p.tunnus, kohde_idt: rajattava ? kohdeArvo : null,
                        lisaa_rooli: rooliPuuttuu && lisaaRooli, koko_yritys_ok: kohdeArvo !== null && eiRajattavat.length > 0, peruste: peruste || null }))}>
                      {t("oik.tallenna")}</button>
                    <button className="nappi nappi--kevyt" onClick={() => setAuki(null)}>{t("oik.peru")}</button>
                  </div>
                </div>
              ) : (
                <div className="oik__napit oik__alanapit">
                  {!voimassa && <button className="nappi nappi--kevyt" disabled={tallennetaan} onClick={() => avaaPaketti(p)} data-anna-paketti={p.tunnus}>{t("oik.anna")}</button>}
                  {voimassa && rajattava && <button className="nappi nappi--kevyt" disabled={tallennetaan} onClick={() => avaaPaketti(p)}>{t("oik.muuta")}</button>}
                  {voimassa && <button className="nappi nappi--kevyt" disabled={tallennetaan} data-pura-paketti={p.tunnus}
                    onClick={() => { if (window.confirm(t("oik.pura_vahvista", { nimi: pakettiNimi(p.tunnus) }))) void teko(() => api.puraPaketti(voimassa.id)); }}>{t("oik.pura")}</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {[...syyt].map(([syy, nimet]) => (
        <p key={syy} className="hint" data-paketit-ei-talle={nimet.length}>{t("oik.ei_talle", { paketit: nimet.join(", "), syy })}</p>
      ))}
        </>);
      })()}

      <details className="oik__lisa" data-lisaasetukset="">
        <summary>{t("oik.lisaasetukset", { n: annettuja })}</summary>
        {oikeusluettelo.length > 12 && <input type="search" placeholder={t("oik.hae_oikeus")} value={haku} onChange={(e) => setHaku(e.target.value)} data-oikeushaku="" style={{ margin: "6px 0" }} />}
        {ryhmat.map((r) => {
          const rivit = oikeudetRyhmassa(r);
          if (!rivit.length) return null;
          return (
            <div key={r} className="oik__ryhma" data-oikeusryhma={r}>
              <h4 className="oik__ryhmaotsikko" data-ryhmaotsikko={r}>{t(`oik.ryhma.${r}` as KaannosAvain)} · {rivit.length}
                {(() => { const n = rivit.filter((o) => yksittain.has(o.tunnus) || paketista.has(o.tunnus)).length; return n ? ` · ${t("oik.n_annettu", { n })}` : ""; })()}</h4>
              <div className="oik__ryhmarivit">
              {rivit.map((o) => {
                const oma = yksittain.get(o.tunnus);
                const onYksittain = yksittain.has(o.tunnus);
                const pak = [...(paketista.get(o.tunnus) ?? [])];
                const pois = o.moduuli_kaytossa === false;
                const annettu = onYksittain || pak.length > 0;
                const nimi = kantaTeksti("oikeus", o.tunnus, "nimi", o.nimi);
                return (
                  <div key={o.tunnus} className={"oik__oikeus" + (annettu ? " oik__oikeus--on" : "") + (pois ? " oikeusrivi--pois" : "")}
                       data-oikeus={o.tunnus} data-oikeus-annettu={annettu ? "1" : "0"}>
                    <div className="oik__rivi oik__rivi--yksi">
                      <span className="oik__nimi" title={nimi}>{nimi}</span>
                      {o.kuvaus && <InfoNappi nimi={nimi}>
                        <span data-oikeus-kuvaus="">{kantaTeksti("oikeus", o.tunnus, "kuvaus", o.kuvaus)}</span>
                        {pois && <span className="info__lisa">{t("kay.moduuli_ei_kaytossa")}</span>}
                      </InfoNappi>}
                      <span className="oik__napit">
                        {oikAuki !== o.tunnus && !pois && <button className="nappi nappi--kevyt" disabled={tallennetaan} data-oikeus-muokkaa={o.tunnus}
                          onClick={() => { setOikAuki(o.tunnus); setOikArvo(onYksittain ? oma ?? null : null); setAuki(null); setPeruste(""); }}>
                          {onYksittain ? t("oik.muuta") : t("oik.anna_oikeus")}</button>}
                        {onYksittain && <button className="nappi nappi--kevyt" disabled={tallennetaan} data-oikeus-poista={o.tunnus}
                          onClick={() => void teko(() => api.oikeudenLaajuus(kayttajaId, o.tunnus, []))}>{t("oik.poista_oikeus")}</button>}
                      </span>
                    </div>
                    {annettu && (
                      <div className="oik__tila" data-oikeus-tila="">
                        {onYksittain && (oma === null || oma === undefined ? t("oik.annettu_koko") : t("oik.annettu_n", { n: oma.length }))}
                        {pak.length > 0 && <span data-oikeus-paketista={pak.join(",")}>{onYksittain ? " · " : ""}{t("oik.paketista", { paketit: pak.map(pakettiNimi).join(", ") })}</span>}
                      </div>
                    )}
                    {oikAuki === o.tunnus && (
                      <div className="oik__muokkaus">
                        <KohdeValitsin arvo={oikArvo} muuta={setOikArvo} kohteet={kohteet} rajattava={o.kohderajattava} />
                        <input placeholder={t("oik.peruste")} value={peruste} onChange={(e) => setPeruste(e.target.value)} maxLength={500} />
                        <div className="oik__napit">
                          <button className="nappi" data-oikeus-tallenna={o.tunnus} disabled={tallennetaan || (oikArvo !== null && oikArvo.length === 0)}
                            onClick={() => void teko(() => api.oikeudenLaajuus(kayttajaId, o.tunnus, o.kohderajattava ? oikArvo : null, peruste || null))}>{t("oik.tallenna")}</button>
                          <button className="nappi nappi--kevyt" onClick={() => setOikAuki(null)}>{t("oik.peru")}</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          );
        })}
        <p className="hint">{t("oik.arkaluonteiset_erikseen")}</p>
      </details>

      <div className="oik__kopio" data-oikeus-kopio="">
        {!kopio ? <button className="nappi nappi--kevyt" onClick={() => setKopio({ lahde: null, esikatselu: null })}>{t("oik.kopioi")}</button> : (
          <div className="oik__muokkaus">
            <select value={kopio.lahde ?? ""} data-kopio-lahde=""
              onChange={async (e) => { const l = Number(e.target.value) || null; setKopio({ lahde: l, esikatselu: null });
                if (l) { try { setKopio({ lahde: l, esikatselu: await api.kopioiOikeudet(l, kayttajaId, true) }); } catch (er) { ilmoita(er instanceof ApiError ? virheTeksti(er.virhe, er.message) : t("kay.muutos_virhe")); } } }}>
              <option value="">{t("oik.kopioi_valitse")}</option>
              {tunnukset.filter((x) => x.id !== kayttajaId).map((x) => <option key={x.id} value={x.id}>{x.nimi}</option>)}
            </select>
            {kopio.esikatselu && (() => {
              const e = kopio.esikatselu!; const tyhja = !(e.paketit?.length) && !(e.yksittaiset?.length);
              return tyhja ? <p className="hint">{t("oik.kopioi_tyhja")}</p> : (
                <div data-kopio-esikatselu="">
                  <p>{t("oik.kopioi_saa")}</p>
                  <ul>
                    {(e.paketit ?? []).map((p) => <li key={"p" + p.paketti}><strong>{pakettiNimi(p.paketti)}</strong> (<KohdeTeksti idt={p.kohde_idt} kohteet={kohteet} />)</li>)}
                    {(e.yksittaiset ?? []).map((y) => <li key={"o" + y.oikeus}>{oikeusNimi(oikeusluettelo, y.oikeus)} (<KohdeTeksti idt={y.kohde_idt} kohteet={kohteet} />)</li>)}
                  </ul>
                  <p className="hint">{t("oik.kopioi_ei")}</p>
                </div>);
            })()}
            <div className="oik__napit">
              <button className="nappi" disabled={tallennetaan || !kopio.lahde || !kopio.esikatselu} data-kopio-vahvista=""
                onClick={() => void teko(async () => {
                  const r = await api.kopioiOikeudet(kopio.lahde!, kayttajaId, false);
                  const hyl = (r.tulos ?? []).filter((x) => !x.ok);
                  if (hyl.length) throw new ApiError(409, "kopio_osittain", hyl.map((x) => `${x.mita}: ${x.viesti ?? ""}`).join(" · "));
                })}>{t("oik.kopioi_vahvista")}</button>
              <button className="nappi nappi--kevyt" onClick={() => setKopio(null)}>{t("oik.peru")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Joukkoanto Tunnukset-näkymässä: valitut tunnukset → «Anna paketti». Jokainen tunnus saa oman myöntönsä palvelimella. */
export function PakettiJoukolle({ valitut, kohteet, oikeusluettelo, valmis, ilmoita }: {
  valitut: number[]; kohteet: Kohde[]; oikeusluettelo: Oikeus[]; valmis: () => Promise<void>; ilmoita: (t: string | null) => void;
}) {
  const [luettelo, setLuettelo] = useState<OikeuspakettiLuettelo | null>(PAKETIT_VALIMUISTI);
  const [paketti, setPaketti] = useState("");
  const [kohdeArvo, setKohdeArvo] = useState<number[] | null>(null);
  const [lisaaRooli, setLisaaRooli] = useState(false);
  const [tehdaan, setTehdaan] = useState(false);
  useEffect(() => { if (!luettelo) void api.oikeuspaketit().then((l) => { PAKETIT_VALIMUISTI = l; setLuettelo(l); }); }, [luettelo]);
  if (!luettelo) return null;
  const p = luettelo.paketit.find((x) => x.tunnus === paketti);
  const rajattava = !!p && p.oikeudet.some((o) => oikeusluettelo.find((x) => x.tunnus === o)?.kohderajattava);
  return (
    <div className="oik__joukko" data-paketti-joukolle="">
      {!valitut.length ? <span className="dim">{t("oik.joukko_valitse")}</span> : (
        <>
          <select value={paketti} onChange={(e) => { setPaketti(e.target.value); setKohdeArvo(null); setLisaaRooli(false); }} data-joukko-paketti="">
            <option value="">{t("oik.valitse_paketti")}</option>
            {luettelo.paketit.map((x) => <option key={x.tunnus} value={x.tunnus}>{pakettiNimi(x.tunnus)}</option>)}
          </select>
          {p && rajattava && <KohdeValitsin arvo={kohdeArvo} muuta={setKohdeArvo} kohteet={kohteet} rajattava />}
          {p && (p.rooli || p.ehdotettuRooli) && (
            <label className={"valinta" + (lisaaRooli ? " valinta--on" : "")}>
              <input type="checkbox" checked={lisaaRooli} onChange={() => setLisaaRooli(!lisaaRooli)} /> {t("oik.vaatii_roolin", { rooli: roolinNimi((p.rooli ?? p.ehdotettuRooli)!) })}
            </label>
          )}
          <button className="nappi" disabled={tehdaan || !p || (kohdeArvo !== null && kohdeArvo.length === 0)} data-joukko-anna=""
            onClick={async () => {
              setTehdaan(true); ilmoita(null);
              try {
                const r = await api.joukkoPaketti({ kayttaja_idt: valitut, paketti, kohde_idt: rajattava ? kohdeArvo : null, lisaa_rooli: lisaaRooli, koko_yritys_ok: true });
                ilmoita(t("oik.joukko_tulos", { myonnetty: r.myonnetty, valittu: r.valittu }) + (r.hylatty.length ? " · " + r.hylatty.map((h) => `#${h.kayttaja_id}: ${h.viesti ?? h.virhe}`).join(" · ") : ""));
                await valmis();
              } catch (e) { ilmoita(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.muutos_virhe")); }
              finally { setTehdaan(false); }
            }}>{t("oik.joukko_anna", { n: valitut.length })}</button>
        </>
      )}
    </div>
  );
}
